import { AlertTriangle, ArrowRight, Lock, MapPin, UserRound } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';

// What a gated action actually means, drawn rather than described.
//
// The stored action is ids, because that is what the server executes. Rendered
// as JSON it read "booking 5d7df574-… → table f51e78bd-…", which tells an
// operator nothing: not who is moving, not away from what, not into which
// group. This shows the same before-and-after the matching board does, so the
// question "is this a good move?" can actually be answered from the card.

function GuestChip({ guest }: { guest: AdminAPI.DecisionGuest }) {
  return (
    <li
      className={[
        'flex items-center gap-1.5 px-2 py-1 rounded-md text-xs',
        guest.isSubject
          ? 'bg-amber-100 dark:bg-amber-900/40 ring-1 ring-amber-400 dark:ring-amber-600 font-semibold'
          : 'bg-neutral-100 dark:bg-neutral-800',
      ].join(' ')}
    >
      {guest.isSubject && <UserRound className="w-3 h-3 shrink-0" />}
      <span className="truncate">{guest.name}</span>
      {guest.notified && (
        <span title="Already sent their table details" className="text-[10px] text-dark-muted">
          ✉
        </span>
      )}
      {guest.flaggedForReview && (
        <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" aria-label="flagged for review" />
      )}
    </li>
  );
}

function TablePanel({
  table,
  label,
  tone,
}: {
  table: AdminAPI.DecisionTable | null;
  label: string;
  tone: 'from' | 'to';
}) {
  if (!table) {
    return (
      <div className="flex-1 min-w-[200px] rounded-lg border border-dashed border-neutral-300 dark:border-neutral-600 p-3">
        <div className="text-[10px] uppercase tracking-wide text-dark-muted mb-1">{label}</div>
        <div className="text-xs text-dark-muted">
          {tone === 'from' ? 'Not seated anywhere yet' : 'No table (the guest is dropped)'}
        </div>
      </div>
    );
  }

  const seated = table.guests.length;
  // Over capacity is the one that puts someone on no chair, so call it out.
  const over = seated > table.capacity;

  return (
    <div className="flex-1 min-w-[200px] rounded-lg border border-neutral-200 dark:border-neutral-700 p-3 bg-white dark:bg-neutral-900">
      <div className="text-[10px] uppercase tracking-wide text-dark-muted mb-1">{label}</div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-semibold text-sm">{table.restaurantName ?? 'Unknown venue'}</span>
        {table.locked && (
          <span
            title="Locked: these guests have been told this table"
            className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-neutral-200 dark:bg-neutral-700"
          >
            <Lock className="w-2.5 h-2.5" /> locked
          </span>
        )}
        {table.reservationStatus && table.reservationStatus !== 'reserved' && table.reservationStatus !== 'confirmed' && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
            {table.reservationStatus.replace(/_/g, ' ')}
          </span>
        )}
      </div>
      <div className="text-xs text-dark-muted mt-0.5 flex items-center gap-2 flex-wrap">
        {table.reservationName && <span>under “{table.reservationName}”</span>}
        <span className={over ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
          {seated}/{table.capacity} seated{over ? ' — over capacity' : ''}
        </span>
      </div>
      <ul className="flex flex-wrap gap-1 mt-2">
        {table.guests.map((g) => (
          <GuestChip key={g.bookingId} guest={g} />
        ))}
        {seated === 0 && <li className="text-xs text-dark-muted">empty</li>}
      </ul>
    </div>
  );
}

export default function DecisionContextView({ context }: { context: AdminAPI.DecisionContext }) {
  if (context.kind === 'batch') {
    return (
      <div className="space-y-3">
        {(context.items ?? []).map((item, i) => (
          <DecisionContextView key={item.subject?.bookingId ?? i} context={item} />
        ))}
      </div>
    );
  }

  if (context.kind === 'create_restaurant') {
    const r = context.proposedRestaurant;
    return (
      <div className="mt-2 rounded-lg border border-neutral-200 dark:border-neutral-700 p-3 bg-white dark:bg-neutral-900">
        <div className="text-[10px] uppercase tracking-wide text-dark-muted mb-1">New venue proposed</div>
        <div className="font-semibold text-sm">{r?.name ?? 'Unnamed venue'}</div>
        {r?.address && (
          <div className="text-xs text-dark-muted flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" /> {r.address}
          </div>
        )}
      </div>
    );
  }

  const heading =
    context.kind === 'cant_be_seated'
      ? `Drop ${context.subject?.name ?? 'this guest'} from the event`
      : `Move ${context.subject?.name ?? 'this guest'}`;

  return (
    <div className="mt-2">
      <div className="text-xs font-medium mb-1.5">{heading}</div>
      <div className="flex items-stretch gap-2 flex-wrap sm:flex-nowrap">
        <TablePanel table={context.fromTable} label="Currently at" tone="from" />
        <div className="hidden sm:flex items-center text-dark-muted shrink-0">
          <ArrowRight className="w-4 h-4" />
        </div>
        <TablePanel table={context.toTable} label="Would move to" tone="to" />
      </div>
      {context.subject?.specialRequests && (
        <div className="mt-1.5 text-xs text-dark-muted">
          <span className="font-medium">Their requests:</span> {context.subject.specialRequests}
        </div>
      )}
    </div>
  );
}
